import { AccessLevelEnum } from '@ftr/distribution-contracts'
import { LocalDate, LocalTime } from '@js-joda/core'
import z from 'zod'

const IdNamePairSchema = z.object({
  id: z.uuid(),
  name: z.string(),
})
export type IdNamePair = z.infer<typeof IdNamePairSchema>

const SerializedCartTimeSchema = z.object({
  startTime: z.string(),
  endTime: z.string().nullable(),
})

const CartTimeSchema = SerializedCartTimeSchema.transform(serializedCartTimeSchema => ({
  startTime: LocalTime.parse(serializedCartTimeSchema.startTime),
  endTime: serializedCartTimeSchema.endTime ? LocalTime.parse(serializedCartTimeSchema.endTime) : null,
}))

export type CartTime = z.infer<typeof CartTimeSchema>

const CartItemRuleSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  accessLevel: AccessLevelEnum,
  publicStatement: z.string(),
})

const SerializedCartItemSchema = SerializedCartTimeSchema.extend({
  id: z.uuid(),
  sessionId: z.uuid(),
  courthouse: IdNamePairSchema,
  courtroom: IdNamePairSchema,
  recordingDate: z.string(),
  rule: CartItemRuleSchema,
  requestedTimes: SerializedCartTimeSchema.array(),
})

const CartItemSchema = SerializedCartItemSchema.transform(serializedCartItemSchema => ({
  ...serializedCartItemSchema,
  recordingDate: LocalDate.parse(serializedCartItemSchema.recordingDate),
  startTime: LocalTime.parse(serializedCartItemSchema.startTime),
  endTime: serializedCartItemSchema.endTime ? LocalTime.parse(serializedCartItemSchema.endTime) : null,
  requestedTimes: serializedCartItemSchema.requestedTimes.map(times => CartTimeSchema.parse(times)),
}))

export type SerializedCartItem = z.infer<typeof SerializedCartItemSchema>
export type CartItem = z.infer<typeof CartItemSchema>

const SerializedCartStateModelSchema = z.object({
  courtSystem: IdNamePairSchema.optional(),
  cartItems: SerializedCartItemSchema.array(),
})

export const CartStateModelSchema = SerializedCartStateModelSchema.transform(serializedCartStateSchema => ({
  ...serializedCartStateSchema,
  cartItems: serializedCartStateSchema.cartItems.map(item => CartItemSchema.parse(item)),
}))

export type SerializedCartStateModel = z.infer<typeof SerializedCartStateModelSchema>
export type CartStateModel = z.infer<typeof CartStateModelSchema>

export interface CartGroup {
  key: string
  courthouse: IdNamePair
  courtroom: IdNamePair
  recordingDate: LocalDate
  items: CartItem[]
}

export const CART_STATE_DEFAULT: CartStateModel = { courtSystem: undefined, cartItems: [] }
